import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { faChartSimple } from '@fortawesome/pro-light-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import EmptyState from 'components/common/EmptyState';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { EmailMarketingPath } from 'routes/Paths';
import { EmailMarketingProvider, useEmailMarketing } from '../EmailMarketingProvider';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useCampaignDetail } from './useCampaignDetail';
import { CAMPAIGN_STATUS_LABELS, describeAudience, formatDateTime, formatNumber, formatPercent } from '../utils';
import { PageHeader } from '../UI/PageHeader/PageHeader';
import { StatusPill } from '../UI/StatusPill/StatusPill';
import { StatCard } from '../UI/StatCard/StatCard';
import { CAMPAIGN_STATUS_TONES } from '../Campaigns/campaignActions';
import TemplatePreviewModal from '../Templates/TemplatePreviewModal';
import styles from './CampaignDetailComponent.module.scss';

const RateBar: React.FC<{ value: number; max: number }> = ({ value, max }) => (
    <span className={styles.rate}>
        <span className={styles.rateValue}>{formatPercent(value)}</span>
        <span className={styles.rateTrack}>
            <span style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
        </span>
    </span>
);

const CampaignDetailContent: React.FC = () => {
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const { templates, duplicateCampaign, locationName, locations, describeLocations } = useEmailMarketing();
    const [search, setSearch] = useState('');
    const [viewingEmail, setViewingEmail] = useState(false);

    const { campaign, loading } = useCampaignDetail(Number(id));
    const template = templates.find(item => item.id === campaign?.templateId) ?? null;
    const backToList = () => history.push(EmailMarketingPath);

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (campaign?.results ?? [])
            .map(result => {
                // Locations may still be loading; keep the row with a placeholder until they arrive.
                const location = locations.find(item => item.id === result.locationId) ?? {
                    id: result.locationId,
                    name: locationName(result.locationId) || `Location ${result.locationId}`,
                    city: '',
                    region: ''
                };
                return {
                    ...result,
                    location,
                    openRate: result.delivered ? result.opens / result.delivered : 0,
                    clickRate: result.delivered ? result.clicks / result.delivered : 0
                };
            })
            .filter(row => !term || `${row.location.name} ${row.location.city}`.toLowerCase().includes(term))
            .sort((a, b) => b.openRate - a.openRate);
    }, [campaign, search, locations, locationName]);

    if (loading) {
        return (
            <div className={styles.loading}>
                <Spinner />
            </div>
        );
    }

    if (!campaign) {
        return (
            <EmptyState
                icon={faChartSimple}
                title="Campaign not found"
                description="It may have been deleted or you don't have access to it."
                showButton
                buttonLabel="Back to campaigns"
                onButtonClick={backToList}
            />
        );
    }

    const maxOpen = Math.max(...rows.map(row => row.openRate), 0);
    const maxClick = Math.max(...rows.map(row => row.clickRate), 0);
    const hasResults = campaign.status === 'sent';
    // API locations have no region yet; hide the column instead of showing it empty.
    const showRegion = rows.some(row => row.location.region);

    return (
        <div className={styles.root}>
            <PageHeader
                onBack={backToList}
                title={campaign.name}
                badge={
                    <StatusPill
                        label={CAMPAIGN_STATUS_LABELS[campaign.status]}
                        tone={CAMPAIGN_STATUS_TONES[campaign.status]}
                    />
                }
                description={`${campaign.sentAt ? `Sent ${formatDateTime(campaign.sentAt)}` : 'Not sent'}${
                    campaign.createdBy ? ` · by ${campaign.createdBy}` : ''
                }`}
                actions={
                    <>
                        {template && (
                            <Button
                                variant="outlined"
                                label="View email"
                                rightIcon={faArrowUpRightFromSquare}
                                onClick={() => setViewingEmail(true)}
                            />
                        )}
                        <Button
                            variant="outlined"
                            label="Duplicate"
                            leftIcon={faCopy}
                            className={styles.hideOnMobile}
                            onClick={() => duplicateCampaign(campaign)}
                        />
                    </>
                }
            />

            <section className={styles.info}>
                <div>
                    <h4>Subject</h4>
                    <p>{campaign.subject}</p>
                    {campaign.previewText && <small>{campaign.previewText}</small>}
                </div>
                <div>
                    <h4>From</h4>
                    <p>Each location&apos;s verified email</p>
                    <small>
                        {campaign.locationIds.length} sender{campaign.locationIds.length === 1 ? '' : 's'}
                    </small>
                </div>
                <div>
                    <h4>Audience</h4>
                    <p>{describeAudience(campaign.recipientType, campaign.tags, campaign)}</p>
                    <small>{describeLocations(campaign.locationIds)}</small>
                </div>
            </section>

            <div className={styles.kpis}>
                <StatCard
                    label="Recipients"
                    value={formatNumber(campaign.recipients)}
                    hint="After removing duplicates"
                />
                <StatCard
                    label="Delivered"
                    value={formatNumber(campaign.delivered)}
                    hint={
                        campaign.recipients
                            ? `${formatPercent(campaign.delivered / campaign.recipients)} of recipients`
                            : undefined
                    }
                />
                <StatCard
                    label="Open rate"
                    value={formatPercent(campaign.openRate)}
                    hint="Industry avg. 33%"
                />
                <StatCard
                    label="Click rate"
                    value={formatPercent(campaign.clickRate)}
                    hint="Industry avg. 2.9%"
                />
            </div>

            <section className={styles.results}>
                <div className={styles.resultsHeader}>
                    <div>
                        <h3>Results by location</h3>
                        <p>Compare how each location&apos;s customers engaged with this campaign.</p>
                    </div>
                </div>

                {hasResults ? (
                    <>
                        <SearchBar
                            searchText={search}
                            placeholder="Search locations"
                            onChangeSearchText={setSearch}
                        />
                        <Table
                            className={styles.table}
                            firstColumnFixed
                        >
                            <TableHeader>
                                <TableHeaderCell>Location</TableHeaderCell>
                                {showRegion && <TableHeaderCell>Region</TableHeaderCell>}
                                <TableHeaderCell className={styles.numeric}>Recipients</TableHeaderCell>
                                <TableHeaderCell className={styles.numeric}>Delivered</TableHeaderCell>
                                <TableHeaderCell>Open rate</TableHeaderCell>
                                <TableHeaderCell>Click rate</TableHeaderCell>
                            </TableHeader>
                            <TableBody>
                                {rows.map(row => (
                                    <TableRow key={row.locationId}>
                                        <TableCell className={styles.locationCell}>
                                            <span className={styles.locationName}>{row.location.name}</span>
                                            <span className={styles.city}>{row.location.city}</span>
                                        </TableCell>
                                        {showRegion && <TableCell>{row.location.region}</TableCell>}
                                        <TableCell className={styles.numeric}>{formatNumber(row.recipients)}</TableCell>
                                        <TableCell className={styles.numeric}>{formatNumber(row.delivered)}</TableCell>
                                        <TableCell>
                                            <RateBar
                                                value={row.openRate}
                                                max={maxOpen}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <RateBar
                                                value={row.clickRate}
                                                max={maxClick}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                ) : (
                    <div className={styles.pending}>
                        <FontAwesomeIcon icon={faChartSimple} />
                        <span>
                            {campaign.status === 'failed'
                                ? 'This campaign failed to send, so there are no results.'
                                : 'Results appear here once the campaign finishes sending.'}
                        </span>
                    </div>
                )}
            </section>

            {viewingEmail && template && (
                <TemplatePreviewModal
                    template={{ ...template, name: campaign.name, subjectDefault: campaign.subject }}
                    locationName={locationName(campaign.locationIds[0] ?? null)}
                    onClose={() => setViewingEmail(false)}
                />
            )}
        </div>
    );
};

export const CampaignDetailComponent: React.FC = () => (
    <EmailMarketingProvider>
        <CampaignDetailContent />
    </EmailMarketingProvider>
);

export default CampaignDetailComponent;
